import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';

import { ApiService } from '../../core/services/api.service';
import { BugsApiService } from './bugs.service';
import {
  BUG_CATEGORY_OPTIONS,
  BUG_SEVERITY_OPTIONS,
  BUG_STATUS_OPTIONS,
  BugDto
} from './bugs.models';

interface ProjectOption { id: string; name: string; }
interface TaskOption { id: string; title: string; projectId: string; }
interface IssueOption { id: string; key: string; summary: string; }

@Component({
  selector: 'app-bugs',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSelectModule,
    MatProgressSpinnerModule, MatTableModule
  ],
  templateUrl: './bugs.component.html',
  styleUrl: './bugs.component.scss'
})
export class BugsComponent implements OnInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly bugsApi = inject(BugsApiService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectOption[]>([]);
  readonly tasks = signal<TaskOption[]>([]);
  readonly issues = signal<IssueOption[]>([]);
  readonly bugs = signal<BugDto[]>([]);

  filterProjectId = '';
  showCreate = false;
  projectId = '';
  relatedTaskId = '';
  relatedIssueId = '';
  title = '';
  description = '';
  severity = 1;
  category = 0;
  steps = '';

  readonly severityOptions = BUG_SEVERITY_OPTIONS;
  readonly categoryOptions = BUG_CATEGORY_OPTIONS;
  readonly statusOptions = BUG_STATUS_OPTIONS;
  readonly columns = ['title', 'severity', 'category', 'status', 'relatedTaskId', 'actions'];

  ngOnInit(): void {
    this.loadProjects();
    this.loadBugs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.api.getPaged<ProjectOption>('v1/projects', { page: 1, pageSize: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.projects.set((page.items ?? []).map(p => ({ id: String(p.id), name: p.name }))),
        error: () => this.projects.set([])
      });
  }

  loadBugs(): void {
    this.loading.set(true);
    this.bugsApi.list({ projectId: this.filterProjectId || undefined, pageSize: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.bugs.set(page.items ?? []);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(err?.message || 'Failed to load bugs.');
          this.loading.set(false);
        }
      });
  }

  onFilterChange(): void {
    this.loadBugs();
  }

  onProjectChange(projectId: string): void {
    this.relatedTaskId = '';
    this.relatedIssueId = '';
    if (!projectId) {
      this.tasks.set([]);
      this.issues.set([]);
      return;
    }
    this.api.getPaged<TaskOption>('v1/tasks', { page: 1, pageSize: 100, projectId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.tasks.set((page.items ?? []).map((t: any) => ({
          id: String(t.id ?? t.Id),
          title: t.title ?? t.Title,
          projectId
        }))),
        error: () => this.tasks.set([])
      });
    this.api.getPaged<IssueOption>('v1/issues', { page: 1, pageSize: 100, projectId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.issues.set((page.items ?? []).map((i: any) => ({
          id: String(i.id ?? i.Id),
          key: i.key ?? i.Key,
          summary: i.summary ?? i.Summary
        }))),
        error: () => this.issues.set([])
      });
  }

  create(): void {
    if (!this.projectId || !this.relatedTaskId || !this.title.trim()) {
      this.error.set('Project, related task, and title are required.');
      return;
    }
    this.saving.set(true);
    this.bugsApi.create({
      projectId: this.projectId,
      relatedTaskId: this.relatedTaskId,
      relatedIssueId: this.relatedIssueId || undefined,
      title: this.title.trim(),
      description: this.description.trim() || undefined,
      severity: this.severity,
      category: this.category,
      stepsToReproduce: this.steps.trim() || undefined
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving.set(false);
        this.showCreate = false;
        this.title = '';
        this.description = '';
        this.steps = '';
        this.relatedTaskId = '';
        this.relatedIssueId = '';
        this.loadBugs();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create bug.');
        this.saving.set(false);
      }
    });
  }

  updateStatus(bug: BugDto, status: number): void {
    this.bugsApi.update(bug.id, { status }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadBugs(),
      error: (err) => this.error.set(err?.message || 'Failed to update bug.')
    });
  }

  deleteBug(bug: BugDto): void {
    if (!confirm(`Delete bug "${bug.title}"?`)) return;
    this.bugsApi.delete(bug.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadBugs(),
      error: (err) => this.error.set(err?.message || 'Failed to delete bug.')
    });
  }

  openBug(id: string): void {
    this.router.navigate(['/bugs', id]);
  }

  severityLabel(v: number): string {
    return this.severityOptions.find(s => s.value === v)?.label ?? String(v);
  }

  categoryLabel(v: number): string {
    return this.categoryOptions.find(c => c.value === v)?.label ?? String(v);
  }

  statusLabel(v: number): string {
    return this.statusOptions.find(s => s.value === v)?.label ?? String(v);
  }

  taskTitle(id?: string): string {
    if (!id) return '—';
    return this.tasks().find(t => t.id === id)?.title ?? id.slice(0, 8);
  }
}
